import { useCurrentUser } from '@cma/app'
import { toast } from '@cma/common'
import {
  Collision,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import React, { createContext, useContext, useState } from 'react'
import { useContent } from './ContentContext'
import { useMoveFiles } from './useMoveFiles'
import { usePlatformContent } from './usePlatformContent'

interface DragContentContextType {
  currentDraggingFile: string | null
  onDragEnd: (event: DragEndEvent) => void
  getDraggableFiles: (currentFileId: string) => any[]
  shouldShowAsGhost: (fileId: string) => boolean
}

const DragContentContext = createContext<DragContentContextType>({
  currentDraggingFile: null,
  onDragEnd: () => {},
  getDraggableFiles: () => [],
  shouldShowAsGhost: () => false
})

interface DragContentProviderProps {
  children: React.ReactNode
}

export function DragContentProvider({ children }: DragContentProviderProps) {
  const { filteredContents, selectedContents, setMovingFileIds } = useContent()
  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: { platformContent } = {} } = usePlatformContent({
    id: currentUser?.account?.id || ''
  })

  const { mutate: moveFiles } = useMoveFiles({
    accountId: currentUser?.account?.id || ''
  })

  const [currentDraggingFile, setCurrentDraggingFile] = useState<string | null>(
    null
  )

  const getDraggableFiles = (currentFileId: string) => {
    const currentFile = filteredContents.find((f) => f.id === currentFileId)
    if (!currentFile || currentFile.__typename === 'Folder') return []

    if (selectedContents.includes(currentFileId)) {
      return filteredContents.filter(
        (content) =>
          selectedContents.includes(content.id) &&
          content.__typename !== 'Folder'
      )
    }

    return [currentFile]
  }

  const shouldShowAsGhost = (fileId: string) => {
    if (!currentDraggingFile || currentDraggingFile === fileId) return false
    const draggableFiles = getDraggableFiles(currentDraggingFile)
    return draggableFiles.some((file) => file.id === fileId)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 }
    }),
    useSensor(KeyboardSensor)
  )

  const onDragStart = (event: DragStartEvent) => {
    const rawId = String(event.active.id)
    const fileId = rawId.replace('PlatformClientContent-', '')
    setCurrentDraggingFile(fileId)
  }

  const onDragCancel = () => {
    setCurrentDraggingFile(null)
  }

  const onDragEnd = (event: DragEndEvent) => {
    setCurrentDraggingFile(null)

    const overId = event.over?.id
    if (!overId) return

    const destinationId = String(overId)
    if (!destinationId.startsWith('Folder-')) return

    const targetFolderId = destinationId.replace('Folder-', '')
    const draggedId = String(event.active.id).replace(
      'PlatformClientContent-',
      ''
    )

    const draggedFile = filteredContents.find((f) => f.id === draggedId)
    if (!draggedFile || draggedFile.__typename === 'Folder') return

    let filesToMove: string[] = []
    if (selectedContents.includes(draggedId)) {
      filesToMove = filteredContents
        .filter(
          (f) => selectedContents.includes(f.id) && f.__typename !== 'Folder'
        )
        .map((f) => f.id)
    } else {
      filesToMove = [draggedId]
    }

    setMovingFileIds(filesToMove)

    moveFiles(
      {
        ids: filesToMove,
        folderId: targetFolderId
      },
      {
        onSuccess() {
          const targetFolder = platformContent?.folders?.find(
            (f) => f.id === targetFolderId
          )
          const folderName = targetFolder?.displayName || 'Folder'
          toast(
            `${filesToMove.length} file(s) moved to ${folderName} successfully`,
            {
              variant: 'success'
            }
          )
        },
        onError(error) {
          toast(error.message || 'Failed to move files', { variant: 'error' })
        },
        onSettled() {
          setMovingFileIds([])
        }
      }
    )
  }

  return (
    <DragContentContext.Provider
      value={{
        currentDraggingFile,
        onDragEnd,
        getDraggableFiles,
        shouldShowAsGhost
      }}>
      <DndContext
        // autoScroll={viewMode === 'table'}
        sensors={sensors}
        collisionDetection={edgeProximity(30)}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}>
        {children}
      </DndContext>
    </DragContentContext.Provider>
  )
}

export function useDragContent() {
  const context = useContext(DragContentContext)
  if (context === undefined) {
    throw new Error('useDragContent must be used within a DragContentProvider')
  }
  return context
}

const edgeProximity =
  (px = 16): CollisionDetection =>
  (args) => {
    const inside = pointerWithin(args)
    if (inside.length) return inside

    const p = args.pointerCoordinates
    if (!p) return []

    const items = args.droppableContainers
      .map((c) => {
        const r = c.rect.current
        if (!r) return null
        const dx = Math.max(r.left - p.x, 0, p.x - r.right)
        const dy = Math.max(r.top - p.y, 0, p.y - r.bottom)
        return { id: c.id, d: Math.hypot(dx, dy) }
      })
      .filter((x): x is { id: string; d: number } => !!x)
      .filter((x) => x.d <= px)
      .sort((a, b) => a.d - b.d)

    if (!items.length) return []

    const best: Collision = { id: items[0].id }
    return [best]
  }
